import { CatalogItemInOrder } from 'entities/CatalogItem/model/types/CatalogItemSchema';

export interface ComparablePriceEntry {
    sellPrice: number;
    availableQuantity: number;
}

export interface ItemSavingsResult {
    baselinePrice: number;
    bestAchievableTotal: number;
    pricePaid: number;
    totalQuantity: number;
    comparableSupplierCount: number;
    savings: number;
    potentialSavings: number;
    isBestPriceSelected: boolean;
    isEligible: boolean;
    savingsPercentage: number;
}

export interface OrderSavingsResult {
    totalSavings: number;
    averageSavingsPercentage: number;
    itemsCount: number;
}

/**
 * Returns comparable price entries (in-stock only) for an item.
 * Post-submission: uses item.allCheckResults (0-stock excluded).
 * Pre-submission: uses item.proposals (0-stock excluded).
 */
export const getComparablePriceEntries = (
    item: CatalogItemInOrder,
): ComparablePriceEntry[] => {
    const source = item.allCheckResults?.length
        ? item.allCheckResults
        : item.proposals ?? [];

    return source
        .map((x) => ({
            sellPrice: x.sellPrice ?? 0,
            availableQuantity: x.availableQuantity ?? 0,
        }))
        .filter((e) => e.availableQuantity > 0 && e.sellPrice > 0);
};

/**
 * Average sell price from comparable entries (in-stock only). Returns null if no valid prices.
 */
export const calculateBaselinePriceFromEntries = (
    entries: ComparablePriceEntry[],
): number | null => {
    if (entries.length === 0) return null;
    const validPrices = entries
        .map((e) => e.sellPrice)
        .filter((p) => p > 0 && !Number.isNaN(p));
    if (validPrices.length === 0) return null;
    return validPrices.reduce((sum, p) => sum + p, 0) / validPrices.length;
};

/**
 * Calculates the best achievable total cost to fulfill a required quantity,
 * factoring in each supplier's price and available quantity.
 * Uses a greedy algorithm: sorts by price (ascending) and fills from cheapest first.
 * Returns null if the required quantity cannot be fulfilled with available stock.
 */
export const calculateBestAchievableTotal = (
    entries: ComparablePriceEntry[],
    requiredQuantity: number,
): number | null => {
    if (entries.length === 0 || requiredQuantity <= 0) return null;

    // Sort by price ascending (cheapest first)
    const sorted = [...entries].sort((a, b) => a.sellPrice - b.sellPrice);

    let remainingNeeded = requiredQuantity;
    let totalCost = 0;

    for (const entry of sorted) {
        if (remainingNeeded <= 0) break;

        const unitsToTake = Math.min(entry.availableQuantity, remainingNeeded);
        totalCost += unitsToTake * entry.sellPrice;
        remainingNeeded -= unitsToTake;
    }

    // If we couldn't fulfill the full quantity, return null
    if (remainingNeeded > 0) return null;

    return totalCost;
};

/**
 * Calculates the price paid for an item based on selected proposals.
 */
export const calculatePricePaidForItem = (
    selectedProposals?: Array<{
        price: number;
        selectedQuantity?: number;
    }>,
): number => {
    if (!selectedProposals || selectedProposals.length === 0) return 0;
    return selectedProposals.reduce(
        (total, p) => total + (p.price || 0) * (p.selectedQuantity || 0),
        0,
    );
};

/**
 * Full product-level savings result for an item (pre- and post-submission).
 * Uses proposals (pre) or allCheckResults (post); 0-stock excluded; eligible = 2+ in-stock suppliers.
 * Returns null if not eligible, no selection, or no comparable prices.
 */
export const getItemSavingsResult = (
    item: CatalogItemInOrder,
): ItemSavingsResult | null => {
    const entries = getComparablePriceEntries(item);
    if (entries.length < 2) return null;

    const baselinePrice = calculateBaselinePriceFromEntries(entries);
    if (baselinePrice === null) return null;

    if (!item.selectedProposals || item.selectedProposals.length === 0)
        return null;

    const pricePaid = calculatePricePaidForItem(item.selectedProposals);
    const totalQuantity = item.selectedProposals.reduce(
        (sum, p) => sum + (p.selectedQuantity || 0),
        0,
    );
    if (totalQuantity <= 0) return null;

    const bestAchievableTotal = calculateBestAchievableTotal(
        entries,
        totalQuantity,
    );
    if (bestAchievableTotal === null) return null; // Cannot fulfill quantity

    const baselineTotal = baselinePrice * totalQuantity;

    // Compare pricePaid to bestAchievableTotal (with small epsilon for floating point)
    const isBestPriceSelected =
        Math.abs(pricePaid - bestAchievableTotal) < 0.01;

    // Savings vs average: positive when price paid is below average (regardless of best)
    const savings = Math.max(0, baselineTotal - pricePaid);
    const potentialSavings = !isBestPriceSelected
        ? Math.max(0, pricePaid - bestAchievableTotal)
        : 0;

    const savingsPercentage =
        baselineTotal > 0
            ? ((baselineTotal - pricePaid) / baselineTotal) * 100
            : 0;

    return {
        baselinePrice,
        bestAchievableTotal,
        pricePaid,
        totalQuantity,
        comparableSupplierCount: entries.length,
        savings,
        potentialSavings,
        isBestPriceSelected,
        isEligible: true,
        savingsPercentage,
    };
};

/**
 * Returns full item savings result when item has positive savings vs average (eligible, 2+ in-stock suppliers).
 * Otherwise null. For unfiltered result use getItemSavingsResult.
 */
export const calculateItemSavings = (
    item: CatalogItemInOrder,
): ItemSavingsResult | null => {
    const result = getItemSavingsResult(item);
    if (!result || result.savings <= 0) return null;
    return result;
};

/**
 * Total order savings = sum of item-level savings vs average (price paid below average).
 * Includes all items where savings > 0; does not include potential savings.
 */
export const calculateOrderSavings = (
    items: CatalogItemInOrder[],
): OrderSavingsResult => {
    const results = items
        .map(getItemSavingsResult)
        .filter((r): r is ItemSavingsResult => r !== null && r.savings > 0);

    if (results.length === 0) {
        return {
            totalSavings: 0,
            averageSavingsPercentage: 0,
            itemsCount: 0,
        };
    }

    const totalSavings = results.reduce((sum, r) => sum + r.savings, 0);
    const averageSavingsPercentage =
        results.reduce((sum, r) => sum + r.savingsPercentage, 0) /
        results.length;

    return {
        totalSavings,
        averageSavingsPercentage,
        itemsCount: results.length,
    };
};
